import React, { useState, useEffect } from 'react';
import axios from 'axios';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';

const BACKEND_URL = getBackendUrl();

const StationTrustDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playLockStats, setPlayLockStats] = useState(null);
  const [fingerprintCacheStats, setFingerprintCacheStats] = useState(null);

  const getAuthHeaders = () => {
    const token = storage.getItem('token');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [lockRes, cacheRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cleanup/play-lock-stats`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/admin/dashboard/fingerprint-cache/stats`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      
      setPlayLockStats(lockRes.data);
      setFingerprintCacheStats(cacheRes.data?.cache_stats || cacheRes.data || null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα φόρτωσης δεδομένων');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !playLockStats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Φόρτωση δεδομένων...</span>
      </div>
    );
  }

  const hardCapMinutes = playLockStats?.config?.max_lock_duration_seconds 
    ? Math.round(playLockStats.config.max_lock_duration_seconds / 60) 
    : 30;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Header with Refresh */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          System Performance
        </h2>
        <button
          onClick={fetchData}
          disabled={loading}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:bg-gray-400"
        >
          {loading ? '⏳' : '🔄'} Refresh
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* SMART FINGERPRINT CACHE v2.0 */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-green-500">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          🎵 Smart Fingerprint Cache v2.0 (Εξοικονόμηση AudD Credits)
        </h3>
        
        {/* Primary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
          {/* Cached Songs */}
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              {fingerprintCacheStats?.total_entries || 0}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Cached Songs</p>
          </div>
          
          {/* API Calls Saved */}
          <div className="text-center">
            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
              {fingerprintCacheStats?.api_calls_saved || 0}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">💰 API Calls Saved</p>
          </div>
          
          {/* Identified Songs */}
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {fingerprintCacheStats?.identified_songs || 0}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Identified Songs</p>
          </div>
          
          {/* Estimated Savings */}
          <div className="text-center">
            <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
              ${fingerprintCacheStats?.estimated_savings_usd || '0.00'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">💵 Estimated Savings</p>
          </div>
        </div>

        {/* Secondary Stats - Cache Methods */}
        {(fingerprintCacheStats?.v2_entries_chromaprint > 0 || fingerprintCacheStats?.chromaprint_fuzzy_hits > 0) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-3 bg-gray-50 dark:bg-gray-700/30 rounded">
            <div className="text-center">
              <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                {fingerprintCacheStats?.v2_entries_chromaprint || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Chromaprint Entries</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">
                {fingerprintCacheStats?.exact_hits || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Exact Hits</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                {fingerprintCacheStats?.chromaprint_fuzzy_hits || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">🎯 Fuzzy Hits</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-gray-600 dark:text-gray-400">
                {fingerprintCacheStats?.not_found_entries || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Not Identified</p>
            </div>
          </div>
        )}

        {/* Top Cached Songs */}
        {fingerprintCacheStats?.top_cached_songs?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Top Cached Songs (by hits):
            </h4>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3 max-h-32 overflow-y-auto">
              {fingerprintCacheStats.top_cached_songs.map((song, idx) => (
                <div key={idx} className="flex justify-between text-sm py-1 border-b border-gray-200 dark:border-gray-600 last:border-0">
                  <span className="text-gray-700 dark:text-gray-300">
                    {song.artist} - {song.title}
                  </span>
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    {song.hit_count} hits
                    {song.fuzzy_hit_count > 0 && (
                      <span className="text-orange-500 ml-1">({song.fuzzy_hit_count} fuzzy)</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
          <p className="text-xs text-green-700 dark:text-green-300">
            <strong>💡 Smart Cache v2.0:</strong> Χρήση <strong>Chromaprint</strong> για acoustic fingerprinting - 
            αναγνωρίζει το ίδιο τραγούδι ανεξάρτητα από το σημείο εγγραφής! 
            <br/>
            <span className="text-orange-600 dark:text-orange-400">🎯 Fuzzy hits</span> = cache hits από διαφορετική θέση εγγραφής του ίδιου τραγουδιού.
          </p>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* PLAY IDENTITY LOCK STATS */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-purple-500">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          🔒 Play Identity Lock (Αποφυγή Διπλών Καταγραφών)
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {playLockStats?.active_locks || 0}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Active Locks</p>
          </div>
          
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {playLockStats?.plays_merged || 0}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Plays Merged</p>
          </div>
          
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              {playLockStats?.locks_created || 0}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Locks Created</p>
          </div>
          
          <div className="text-center">
            <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
              {playLockStats?.locks_expired || 0}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Locks Expired</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">Min Lock Window</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {playLockStats?.config?.min_lock_window_seconds || 180}s
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">Duration Factor</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {playLockStats?.config?.lock_window_duration_factor || 0.6}x
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">Hard Cap</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {hardCapMinutes} λεπτά
            </p>
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>💡 Πώς λειτουργεί:</strong> Όταν ανιχνεύεται ένα τραγούδι, δημιουργείται ένα "lock" 
            που εμποδίζει διπλές καταγραφές. Το lock λήγει μετά από {hardCapMinutes} λεπτά max.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StationTrustDashboard;
